import * as React from "react";
import { HelpCircle } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { RefineBackgroundDialog } from "@/components/Backgrounds/RefineBackgroundDialog";
import { HoloShimmer } from "@/components/Holo";
import { useBackgroundRequests } from "@/components/Backgrounds/useBackgroundRequests";
import { Button, confirmDialog, Tooltip } from "@/components/ui";
import { BackgroundAsset, BackgroundRatio, BackgroundRequest } from "@/services/bedrock/dtos/BackgroundAsset";
import { DesignBackground, DesignPackageDetail, DesignPackageItem } from "@/services/bedrock/dtos/DesignPackage";
import { designKeys } from "@/services/bedrock/queryKeys";
import { useDynamicPromptsAccess } from "@/utils/useDynamicPromptsAccess";
import { GENERATE_FAILED, useBackgroundMutations } from "../hooks/useBackgroundMutations";
import { usePreviews } from "../hooks/usePreviews";
import { isItemInProgress, slotRatioFor } from "../slideState";
import { AddBackgroundMenu } from "./AddBackgroundMenu";
import { BackgroundPickerDialog } from "./BackgroundPickerDialog";
import { BackgroundPolicyControl } from "./BackgroundPolicyControl";
import { BackgroundTile, BackgroundTileState } from "./BackgroundTile";
import { DisabledReason, inFlightReasonFor } from "./DisabledReason";
import { EditorMode } from "./EditorHeader";
import { GenerateBackgroundsDialog } from "./GenerateBackgroundsDialog";
import { useViewer } from "../viewer";

interface BackgroundsTrayProps {
  pkg: DesignPackageDetail;
  item: DesignPackageItem;
  themeId: number | null;
  mode: EditorMode;
  onOpenHelp(topic: "backgrounds"): void;
}

// The progress text the backend puts on the slide while its background requests are pending; the
// placeholder for viewers who don't follow the requests themselves.
const GENERATING = "Creating background imagery…";

/** A generation or refine this browser started, followed until its first image is tried or it fails. */
interface StartedBatch {
  itemId: number;
  tried: boolean;
}

function placeholderLabel(request: BackgroundRequest | null): string {
  const wait = request?.estimatedWaitSeconds;
  return wait != null && wait >= 60 ? `Creating… about ${Math.ceil(wait / 60)} min` : GENERATING;
}

const PlaceholderTile: React.FC<{ ratio: BackgroundRatio; label: string }> = ({ ratio, label }) => (
  <div className="flex w-16 shrink-0 snap-start flex-col items-center gap-1">
    <div className="w-full" data-testid="background-placeholder" aria-label={label}>
      <HoloShimmer aspectRatio={ratio} showLogo={false} className="rounded-lg" />
    </div>
    <span className="text-center text-[11px] leading-tight text-slate-500">{label}</span>
  </div>
);

const assetIdOf = (asset: BackgroundAsset | DesignBackground) => ("assetId" in asset ? asset.assetId : asset.id);

export const BackgroundsTray: React.FC<BackgroundsTrayProps> = ({ pkg, item, themeId, mode, onOpenHelp }) => {
  const access = useDynamicPromptsAccess();
  const viewer = useViewer();
  const queryClient = useQueryClient();
  const { tryBackground, triedSlide, keepCurrent } = usePreviews(pkg.id, themeId);
  const {
    attachBackground,
    removeBackground,
    setDefault,
    uploadBackground,
    uploadProgress,
    generateBackgrounds,
    refineBackground,
  } = useBackgroundMutations(pkg.id);
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const [generateOpen, setGenerateOpen] = React.useState(false);
  const [refining, setRefining] = React.useState<DesignBackground | null>(null);
  const [batches, setBatches] = React.useState<Record<string, StartedBatch>>({});

  const canEdit = mode === "owner" && access.canEdit;
  const template = item.activeTemplate;
  const backgrounds = template.backgrounds;
  const ratio = slotRatioFor(item, pkg);
  const tried = triedSlide(item.id);
  const inProgress = isItemInProgress(item);
  const uploading = uploadBackground.isPending;
  const inFlightReason = inProgress
    ? inFlightReasonFor(pkg.inFlight, viewer) ?? "Wait for the current change to finish."
    : null;

  const { requests, pending, failedBatch } = useBackgroundRequests({
    designTemplateId: template.id,
    enabled: canEdit,
  });

  // A tried background belongs to one slide on this visit; drop it when the slide changes, the
  // option goes away, or the editor closes (the cache outlives the page, the failure toast doesn't).
  const keepCurrentRef = React.useRef(keepCurrent);
  keepCurrentRef.current = keepCurrent;
  React.useEffect(
    () => () => {
      keepCurrentRef.current();
    },
    []
  );
  const previousItemId = React.useRef(item.id);
  React.useEffect(() => {
    if (previousItemId.current !== item.id) {
      previousItemId.current = item.id;
      keepCurrent();
    }
  }, [item.id, keepCurrent]);
  const triedAssetId = tried?.assetId;
  React.useEffect(() => {
    if (triedAssetId != null && !backgrounds.some(b => b.assetId === triedAssetId)) keepCurrent();
  }, [triedAssetId, backgrounds, keepCurrent]);

  // A request the poll reports done has attached its picture; refetch so the tile shows without
  // waiting for the broadcast.
  const seenDone = React.useRef<Set<number> | null>(null);
  React.useEffect(() => {
    const done = requests.filter(r => r.status === "done").map(r => r.id);
    if (seenDone.current && done.some(id => !seenDone.current!.has(id))) {
      queryClient.invalidateQueries({ queryKey: designKeys.detail(pkg.id) });
    }
    seenDone.current = new Set(done);
  }, [requests, queryClient, pkg.id]);

  // For batches started here: try the first picture once it is on the slide, or tell the coach once
  // when nothing came of it.
  React.useEffect(() => {
    Object.entries(batches).forEach(([batchKey, batch]) => {
      if (batch.itemId !== item.id) return;
      if (failedBatch(batchKey)) {
        toast.error(GENERATE_FAILED);
        setBatches(({ [batchKey]: _, ...rest }) => rest);
        return;
      }
      if (batch.tried) return;
      const landed = requests
        .filter(r => r.batchKey === batchKey && r.status === "done" && r.backgroundAssetId != null)
        .sort((a, b) => a.position - b.position)
        .find(r => backgrounds.some(b => b.assetId === r.backgroundAssetId));
      if (landed) {
        setBatches(current => ({ ...current, [batchKey]: { ...batch, tried: true } }));
        tryBackground(item.id, { assetId: landed.backgroundAssetId! });
      }
    });
  }, [batches, requests, backgrounds, failedBatch, item.id, tryBackground]);

  const follow = (started: BackgroundRequest[]) => {
    const keys = Array.from(new Set(started.map(r => r.batchKey).filter((k): k is string => !!k)));
    if (keys.length === 0) return;
    setBatches(current => ({
      ...current,
      ...Object.fromEntries(keys.map(key => [key, { itemId: item.id, tried: false }])),
    }));
  };

  const tileState = (background: DesignBackground, index: number): BackgroundTileState => {
    if (index === 0) return "default";
    if (setDefault.isPending && setDefault.variables?.assetId === background.assetId) return "setting";
    if (removeBackground.isPending && removeBackground.variables?.assetId === background.assetId) return "removing";
    if (tried?.assetId === background.assetId && (tried.status === "pending" || tried.status === "processing"))
      return "trying";
    return "idle";
  };

  const onTry = (background: DesignBackground, index: number) => {
    if (inProgress) return;
    if (index === 0) keepCurrent();
    else tryBackground(item.id, { assetId: background.assetId });
  };

  const onSetDefault = async (background: DesignBackground) => {
    if (pkg.publishedAt) {
      const confirmed = await confirmDialog({
        title: "Change the default background?",
        description: "Players will see the new default. Posts already made aren't affected.",
        confirmLabel: "Set as default",
        cancelLabel: "Cancel",
      });
      if (!confirmed) return;
    }
    setDefault.mutate({ templateId: template.id, assetId: background.assetId }, { onSuccess: () => keepCurrent() });
  };

  const onRemove = async (background: DesignBackground) => {
    const confirmed = await confirmDialog({
      destructive: true,
      title: "Remove this background?",
      description: "It disappears from the options. Posts already made with it aren't affected.",
      confirmLabel: "Remove",
      cancelLabel: "Cancel",
    });
    if (confirmed) removeBackground.mutate({ templateId: template.id, assetId: background.assetId });
  };

  const attach = async (asset: BackgroundAsset | DesignBackground) => {
    const assetId = assetIdOf(asset);
    await attachBackground.mutateAsync({ templateId: template.id, assetId });
    toast.success("Background added");
    tryBackground(item.id, { assetId });
  };

  const onPick = (asset: BackgroundAsset | DesignBackground) => {
    setPickerOpen(false);
    attach(asset).catch(() => {
      // The mutation hook already toasts the error.
    });
  };

  const onUpload = async (file: File) => {
    try {
      const { assetId } = await uploadBackground.mutateAsync({ templateId: template.id, file });
      setPickerOpen(false);
      tryBackground(item.id, { assetId });
    } catch {
      // The mutation hook already told the coach what went wrong.
    }
  };

  const openGenerate = () => {
    setPickerOpen(false);
    setGenerateOpen(true);
  };

  const onGenerate = async ({ hint, count }: { hint: string; count: 1 | 2 | 3 }) => {
    const { requests: started } = await generateBackgrounds.mutateAsync({ templateId: template.id, hint, count });
    follow(started);
  };

  const onRefine = async (instruction: string) => {
    if (!refining) return;
    const started = await refineBackground.mutateAsync({
      templateId: template.id,
      assetId: refining.assetId,
      instruction,
    });
    follow([started]);
  };

  // The slide card around the tray already names the slide.
  const title = pkg.items.length > 1 ? "Backgrounds for this slide" : "Backgrounds";
  const placeholders: (BackgroundRequest | null)[] =
    pending.length > 0 ? pending : item.progressStatus === GENERATING ? [null] : [];
  const triedIndex = tried ? backgrounds.findIndex(b => b.assetId === tried.assetId) : -1;
  const attachedIds = backgrounds.map(b => b.assetId);

  const renderTiles = () => {
    return (
      <div className="flex flex-col gap-2">
        <div className="flex items-start gap-3 overflow-x-auto snap-x pt-1 pb-3 px-1" data-testid="background-tiles">
          {backgrounds.length === 0 && placeholders.length === 0 && (
            <p className="self-center text-sm text-slate-500">No background photo. Add one to give players options.</p>
          )}
          {backgrounds.map((background, index) => (
            <BackgroundTile
              key={background.assetId}
              background={background}
              ratio={ratio}
              state={inProgress && index > 0 ? "idle" : tileState(background, index)}
              canEdit={canEdit && !inProgress}
              canRemove={index > 0 && backgrounds.length > 1}
              onTry={() => onTry(background, index)}
              onSetDefault={() => void onSetDefault(background)}
              onRefine={() => setRefining(background)}
              onRemove={() => void onRemove(background)}
            />
          ))}
          {placeholders.map((request, i) => (
            <PlaceholderTile
              key={request ? `request-${request.id}` : `placeholder-${i}`}
              ratio={ratio}
              label={placeholderLabel(request)}
            />
          ))}
          {canEdit && (
            <DisabledReason reason={inFlightReason}>
              <AddBackgroundMenu
                disabled={inProgress || uploading}
                onChooseLibrary={() => setPickerOpen(true)}
                onUpload={file => void onUpload(file)}
                onGenerate={() => setGenerateOpen(true)}
                uploading={uploading}
                uploadProgress={uploadProgress}
              />
            </DisabledReason>
          )}
        </div>

        {tried?.status === "success" && triedIndex > 0 && (
          <div className="flex flex-wrap items-center gap-2" data-testid="background-previewing">
            <span className="text-sm text-slate-700">Previewing this background on slide {item.position}</span>
            {canEdit && (
              <Button
                size="sm"
                loading={setDefault.isPending}
                onClick={() => void onSetDefault(backgrounds[triedIndex])}
              >
                Set as default
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={keepCurrent}>
              Keep current
            </Button>
          </div>
        )}
      </div>
    );
  };

  // While the slide shows its old and new versions side by side, backgrounds would only get in the way.
  if (item.candidateTemplate) return null;

  return (
    <section className="flex min-w-0 shrink-0 flex-col gap-2" data-testid="backgrounds-tray">
      <div className="flex items-center gap-1">
        <span className="text-xs font-bold uppercase tracking-wider text-slate-400">{title}</span>
        <Tooltip>
          <Tooltip.Trigger
            render={
              <Button
                variant="ghost"
                size="icon-xs"
                aria-label="How backgrounds work"
                className="text-slate-400 hover:text-slate-700"
                onClick={() => onOpenHelp("backgrounds")}
              />
            }
          >
            <HelpCircle aria-hidden="true" />
          </Tooltip.Trigger>
          <Tooltip.Content>How backgrounds work</Tooltip.Content>
        </Tooltip>
      </div>

      {renderTiles()}

      <BackgroundPolicyControl pkg={pkg} canEdit={canEdit} />

      {canEdit && (
        <>
          <BackgroundPickerDialog
            open={pickerOpen}
            onOpenChange={setPickerOpen}
            itemPosition={item.position}
            ratio={ratio}
            contextType={item.contextType}
            excludeAssetIds={attachedIds}
            onSelect={onPick}
            onUpload={file => void onUpload(file)}
            onGenerate={openGenerate}
            uploading={uploading}
            uploadProgress={uploadProgress}
            pendingRequests={pending}
          />
          <GenerateBackgroundsDialog
            open={generateOpen}
            onOpenChange={setGenerateOpen}
            itemPosition={item.position}
            ratio={ratio}
            contextType={item.contextType}
            excludeAssetIds={attachedIds}
            onAttach={attach}
            onGenerate={onGenerate}
            busy={generateBackgrounds.isPending}
          />
          {refining && (
            <RefineBackgroundDialog
              open
              onOpenChange={open => !open && setRefining(null)}
              asset={{ id: refining.assetId, url: refining.url, focal: refining.focal }}
              ratio={ratio}
              onSubmit={onRefine}
              busy={refineBackground.isPending}
            />
          )}
        </>
      )}
    </section>
  );
};
